import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Grid, Skeleton, Button as MuiButton, IconButton, MenuItem, Avatar, Typography as MuiTypography } from '@mui/material';
import Pagination from 'components/Pagination';
import Search from 'components/Search';
import CustomTable, { StyledTableCell, StyledTableRow } from 'components/Table';
import Typography from 'components/Typography';
import FilterDateModal from 'container/modals/FilterDate';
import {
    colors,
    displayFlexAlignItemsCenterJustifyContentCenter,
    displayFlexAlignItemsCenterJustifyContentSpaceBetween,
    tokens,
} from 'styles';

import { deleteOne, getAllUsers, getUsersByReferral } from 'api/users';
import { useUsers } from 'context/UserContext';
import ConfirmDelete from 'dialogs/ConfirmDelete';
import { getDate, getTime } from 'helpers/data-functions';
import { toast } from 'react-toastify';
import BasicPopover from './ThreeActionsPopover';
import { ArrowDownward, ArrowUpward, Visibility } from '@mui/icons-material';
import Button from 'components/Button';
import StateContainer from 'components/StateContainer';

const PlayerManagement = () => {
    const navigate = useNavigate();

    const { deleteUser: deleteUserFromState } = useUsers();

    const [getReferralUsers, setGetReferralUsers] = useState({
        fetching: true,
        loading: false,
        error: null,
        data: [],
    });

    const [showDateFilterModal, setShowDateFilterModal] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [deleteId, setDeleteId] = useState();
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(9);
    const [inputChange, setInputChange] = useState('');

    const [isSortByDate, setIsSortByDate] = useState(false); // Track sorting by date
    useEffect(() => {
        // Create a copy of the users array to avoid modifying the original array
        const sortedUsers = [...getReferralUsers.data];

        // Sort the copied array based on the condition
        if (isSortByDate) {
            sortedUsers.sort((a, b) => new Date(a.created_date) - new Date(b.created_date));
        } else {
            sortedUsers.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));

            // Sort by a different property or in a different way if needed
        }

        // Set the state with the sorted array
        setFilteredUsers(sortedUsers);
    }, [isSortByDate, getReferralUsers.data]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const [filteredUsers, setFilteredUsers] = useState([]);

    const toggleDeleteOpen = () => {
        setIsDeleteOpen((st) => !st);
    };

    const handleClose = () => setAnchorEl(null);

    const handleClick = (event) => setAnchorEl(event.currentTarget);


    const fetchAllUsers = async () => {
        console.log("fetching")
        try {
            const res = await getAllUsers();

            setGetReferralUsers((st) => ({
                ...st,
                data: res?.data?.data?.users || [],
                fetching: false,
                error: null,
            }));
        } catch (error) {
            setGetReferralUsers((st) => ({
                ...st,
                fetching: false,
                error: error?.response?.data?.message || error.message || 'An error occurred while fetching players.',
            }));
        }
    }


    useEffect(() => {
        fetchAllUsers()
    }, [])

    // useEffect(() => {
    //     if (!getReferralUsers.fetching) return;
    //     (async () => {
    //         try {
    //             const { data } = await getUsersByReferral();
    //             setGetReferralUsers((st) => ({
    //                 ...st,
    //                 data: data.users,
    //                 fetching: false,
    //             }));
    //         } catch (er) {
    //             console.error(er);
    //         } finally {
    //             setGetReferralUsers((st) => ({
    //                 ...st,
    //                 fetching: false,
    //             }));
    //         }
    //     })();
    // }, [getReferralUsers.fetching]);

    React.useEffect(() => {
        const handleScroll = () => setAnchorEl(null);
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [anchorEl]);

    const getFilterDates = (from, to) => {
        setFilteredUsers(
            getReferralUsers.data.filter((el) => new Date(el.created_date) >= from && new Date(el.created_date) <= to),
        );
    };

    const deleteUser = async () => {
        try {
            await deleteOne(deleteId);
            deleteUserFromState(deleteId);
            toast.success('User deleted successfully');
        } catch (err) {
            console.error(err);
        } finally {
            toggleDeleteOpen();
        }
    };

    useEffect(() => {
        if (!getReferralUsers.data) return;
        setFilteredUsers(getReferralUsers.data.slice((page - 1) * 10, page * 10));
    }, [getReferralUsers.data, page]);

    useEffect(() => {
        handleSearch();
    }, [inputChange]);

    const handleSearch = () => {
        const val = inputChange;

        if (!val) return setFilteredUsers(getReferralUsers.data);
        setFilteredUsers(
            getReferralUsers.data
                .filter(
                    (el) => el.id.toLowerCase().includes(val.toLowerCase()) || el.name.toLowerCase().includes(val.toLowerCase()),
                )
                .slice((page - 1) * 10, page * 10),
        );
    };

    return (
        <React.Fragment>
            <Box mb={3} mt={4}>
                <MuiTypography variant='h5' sx={{ color: '#fff', fontWeight: 700, letterSpacing: -0.5 }}>
                    Player Management
                </MuiTypography>
                <MuiTypography variant='body2' sx={{ color: '#8b8fa8', mt: 0.5 }}>
                    View and manage registered players.
                </MuiTypography>
            </Box>
            <Grid container spacing={2} mb={4}>
                <Grid item xs={12} md={10} lg={8}>
                    <Search
                        placeholder='Search player By ID, Name'
                        handleSearch={handleSearch}
                        inputChange={inputChange}
                        setInputChange={setInputChange}
                    />
                </Grid>
            </Grid>
            <Button
                variant={'back36'}
                text='Recent'
                token={tokens.FS15FW600LH18SB}
                width='133px'
                padding={'16px'}
                style={{
                    height: '100%',
                    boxShadow: 'none',
                    marginRight: '1rem',
                    textTransform: 'none',
                    marginBottom: '1rem',
                }}
                onClick={() => setIsSortByDate(!isSortByDate)}
                iconEnd={isSortByDate ? <ArrowDownward /> : <ArrowUpward />}
            />

            <StateContainer
                isLoading={getReferralUsers.fetching}
                isError={!!getReferralUsers.error}
                errorMessage={getReferralUsers.error}
                isEmpty={getReferralUsers.data.length === 0}
                emptyTitle="No Players Found"
                emptyDescription="There are currently no registered players."
                onRetry={fetchAllUsers}
            >
                <CustomTable tableHead={userTableHead}>
                    {filteredUsers
                        .map((el) => (
                            <StyledTableRow key={`${Math.random()}`}>
                                <StyledTableCell align='center'>
                                    <Typography
                                        token={tokens.FS16FW500LH19_2M}
                                        color={colors.whiteFF}
                                        text={el.id.replace(el.id.slice(5, 16).toString(), '******')}
                                    />
                                </StyledTableCell>
                                <StyledTableCell align='center'>
                                    <Typography token={tokens.FS16FW500LH19_2M} color={colors.whiteFF} text={el.name} />
                                </StyledTableCell>
                                <StyledTableCell align='center'>
                                    <Box {...displayFlexAlignItemsCenterJustifyContentCenter} gap={'5px'}>
                                        <Typography token={tokens.FS16FW500LH19_2M} color={colors.whiteFF} text={el.email} />
                                    </Box>
                                </StyledTableCell>

                                <StyledTableCell align='center'>
                                    <Box {...displayFlexAlignItemsCenterJustifyContentCenter} gap={'5px'}>
                                        <Typography
                                            token={tokens.FS16FW500LH19_2M}
                                            color={colors.whiteFF}
                                            text={getDate(new Date(el.created_date))}
                                        />
                                        <Typography
                                            token={tokens.FS16FW500LH19_2M}
                                            color={colors.orange00}
                                            text={getTime(new Date(el.created_date))}
                                        />
                                    </Box>
                                </StyledTableCell>

                                <StyledTableCell align='right'>
                                    <BasicPopover
                                        action1={{
                                            name: 'View Player',
                                            icon: <Visibility />,
                                            onClick: () => navigate(`/player-management/${el.id}`),
                                        }}

                                    />
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                </CustomTable>
            </StateContainer>
            <Box {...displayFlexAlignItemsCenterJustifyContentSpaceBetween} gap={'10px'} mt={'25px'}>
                <Box {...displayFlexAlignItemsCenterJustifyContentCenter} gap={'5px'}>
                    <Typography token={tokens.FS16FW500LH19_2M} color={colors.greyC4} text={'Showing '} />
                    <Typography
                        token={tokens.FS16FW500LH19_2M}
                        color={colors.whiteFF}
                        text={Math.min(page * rowsPerPage, getReferralUsers.data.length)}
                    />
                    <Typography token={tokens.FS16FW500LH19_2M} color={colors.greyC4} text={' Results out of '} />
                    <Typography
                        token={tokens.FS16FW500LH19_2M}
                        color={colors.whiteFF}
                        text={getReferralUsers.data?.length || 0}
                    />
                </Box>
                <Pagination
                    count={Math.ceil(getReferralUsers.data.length / rowsPerPage)}
                    page={page}
                    handleChange={handlePageChange}
                />
            </Box>
            {showDateFilterModal && (
                <FilterDateModal
                    open={showDateFilterModal}
                    handleClose={() => setShowDateFilterModal(false)}
                    handleSubmit={(from, to) => {
                        setShowDateFilterModal(false);
                        getFilterDates(from, to);
                    }}
                />
            )}
            <ConfirmDelete open={isDeleteOpen} handleSuccess={deleteUser} toggleDialog={toggleDeleteOpen} />
        </React.Fragment>
    );
};

const userTableHead = [
    {
        id: 1,
        label: 'Id',
        align: 'center',
    },
    {
        id: 2,
        label: 'Name',
        align: 'center',
    },
    {
        id: 3,
        label: 'Email',
        align: 'center',
    },
    {
        id: 4,
        label: 'Joined On',
        align: 'center',
    },
    {
        id: 6,
        label: '',
        align: 'right',
    },
];

export default PlayerManagement